import { AfterContentInit, Component, Input } from '@angular/core';
import { AppState, PpaModel, PpaReviewStats } from '../ngrx-store/app.state';
import { MatDialog } from '@angular/material/dialog';
import { MoreViewSearchComponent } from '../dialogs/more-view-search/more-view-search.component';
import { CallerService } from '../services/caller.service';
import { Store } from '@ngrx/store';
import { ppaReviewStatsUpdated } from '../ngrx-store/actions/corp-member.actions';
import { animate, style, transition, trigger } from '@angular/animations';
import { finalize } from 'rxjs/operators';

import { PpaActionsService } from '../services/ppa-actions.service'
import { housesNearPpaQueryParams, ppaPlace, ppaTypeIcon } from '../utils/ppa.util'
import { thumbnailUrl } from '../utils/sale.util'
@Component({
  selector: 'app-ppa-content-card',
  templateUrl: './ppa-content-card.component.html',
  styleUrls: ['./ppa-content-card.component.css'],
  animations: [
    trigger('iconChangeAnimation', [
      transition('* <=> *', [
        style({
          transform: `scale(1.5)`,
          opacity: 0
        }),
        animate('.2s 0s ease-out'),
      ])
    ]),
  ],
})
export class PpaContentCardComponent implements AfterContentInit {

  @Input() ppa!: PpaModel;

  /**
   * 'grid' is the default card; 'list' is a row with a small image (PPAs page list view, from sm up);
   * 'row' is the compact row phones get on the PPAs page.
   */
  @Input() layout: 'grid' | 'list' | 'row' = 'grid'

  bookmarkState: boolean = false

  /** A review is on its way - no second one until it's in (the dialog closes before the request). */
  postingReview: boolean = false

  constructor(
    public dialog: MatDialog,
    public callerService: CallerService,
    private store: Store<AppState>,
    private ppaActions: PpaActionsService,
  ) {}

  ngAfterContentInit(): void {
    this.bookmarkState = !!this.ppa?.PPABookmarks?.[0]?.id
  }

  protected get toggleBookmarkIcon(): 'bookmark_border' | 'bookmark' {
    return this.bookmarkState ? 'bookmark' : 'bookmark_border'
  }

  bookmarkPost() {
    if (this.bookmarkState) {
      this.callerService.unBookmarkPpa(this.ppa.id).subscribe((res) => {
        this.bookmarkState = !this.bookmarkState
        this.callerService.showNotification("Bookmark removed!")
      }, (err) => {
        this.callerService.showNotification(err?.error?.message ?? "Failed to remove bookmark item")
      })
    } else {
      this.callerService.bookmarkPpa(this.ppa.id)
      .subscribe({
        next: (res: any) => {
          if (res?.message) {
            this.callerService.showNotification(res.message)
          } else if (res.data?.id) {
            this.bookmarkState = !this.bookmarkState
            this.callerService.showNotification("Bookmarked!")
          }
        },
        error: (err) => {
          this.callerService.showNotification(err?.error?.message ?? "Failed to bookmark item")
        },
      })
    }
  }

  share(): Promise<void> {
    return this.ppaActions.share(this.ppa)
  }

  /**
   * The count, rating and quote with the new review counted (POST /ppa/review sends them), so the
   * card updates without reloading the list. Into the store for lists kept there (/ppa), and onto
   * this card's own copy for lists that aren't (/search).
   */
  private applyReviewStats(stats?: PpaReviewStats | null): void {
    if (!stats) {
      return
    }
    const update: PpaReviewStats = {
      review_count: Number(stats.review_count),
      avg_rating: stats.avg_rating,
      latest_review: stats.latest_review,
    }
    this.ppa = { ...this.ppa, ...update }
    this.store.dispatch(ppaReviewStatsUpdated({ id: this.ppa.id, stats: update }))
  }

  leaveReview(ppa: PpaModel) {
    if (this.postingReview) {
      return
    }
    this.ppaActions
      .review(ppa, () => (this.postingReview = true))
      .pipe(finalize(() => (this.postingReview = false)))
      .subscribe((posted) => this.applyReviewStats(posted.ppa_stats))
  }

  openMorePpaDetails(ppa: PpaModel | any) {
    // @todo: record that this PPA was seen (and for how long)?
    this.dialog.open(MoreViewSearchComponent, {
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      height: '100%',
      width: '100%',
      data: {
        ppa,
      },
      ariaLabel: 'Dialog to show more details about a PPA',
      role: 'dialog',
    })
  }

  /** From the PPA stats (GET /ppas, search); falls back to the loaded reviews where they aren't sent. */
  get reviewCount(): number {
    return this.ppa.review_count ?? this.ppa.Reviews?.length ?? 0
  }

  get rating(): number | null {
    if (this.ppa.avg_rating !== undefined) {
      return this.ppa.avg_rating === null ? null : Number(this.ppa.avg_rating) // MySQL decimals arrive as strings
    }
    const rated = (this.ppa.Reviews ?? []).filter((review) => review.star_rating)
    return rated.length ? rated.reduce((sum, review) => sum + Number(review.star_rating), 0) / rated.length : null
  }

  get corperCount(): number {
    return this.ppa.corper_count ?? 0
  }

  get housesNearby(): number {
    return this.ppa.houses_nearby_count ?? 0
  }

  /** Same place houses_nearby_count counts: the primary location's LGA. */
  get housesQueryParams(): Record<string, string> {
    return housesNearPpaQueryParams(this.ppa)
  }

  /** The newest review that says something (from the list's `latest_review`, else the loaded reviews). */
  get latestQuote(): string | null {
    if (this.ppa.latest_review !== undefined) {
      return this.ppa.latest_review?.trim() || null
    }
    const withComment = (this.ppa.Reviews ?? []).filter((review) => review.comment?.trim())
    if (!withComment.length) {
      return null
    }
    const newest = withComment.reduce((a, b) => (new Date(b.created_at) > new Date(a.created_at) ? b : a))
    return newest.comment.trim()
  }

  get address(): string | null {
    return this.ppa.Locations?.[0]?.address?.trim() || null
  }

  /** The row's thumbnail didn't load - it shows the type's icon instead, like a PPA without photos. */
  thumbnailFailed = false

  /** The row's 64px thumbnail: the first photo, cut small (2x for sharp screens). */
  get rowThumbnail(): { src: string; srcset: string | null } | null {
    const url = this.ppa.Media?.find((photo) => !!photo.url)?.url
    if (!url) return null
    const small = thumbnailUrl(url, 64) ?? url
    return { src: small, srcset: small !== url ? `${small} 1x, ${thumbnailUrl(url, 128)} 2x` : null }
  }

  get typeIconName(): string {
    return ppaTypeIcon(this.ppa.type_of_ppa)
  }

  /** "Ikeja, Lagos". */
  get ppaLocation(): string {
    return ppaPlace(this.ppa.Locations?.[0])
  }
}
